import { generateCpp, describeSerialError } from '@spblock/core';
import { requireWorkspace } from '../lib/workspace';
import { useStore } from '../state/store';
import { compileSketch } from './compile-client';
import { EspFlasher } from './flasher';
import { monitorExistingPort, stopSerialMonitor } from './serial-port';

/**
 * Flash to Board: generate → validate → compile → flash → monitor.
 * Every step reports into the terminal dock and the store phase machine.
 */

export async function flashToBoard(): Promise<void> {
  const store = useStore.getState();
  const { pushLine, setFlashPhase, setFlashProgress, setSerialState, settings, projectName } = store;

  await stopSerialMonitor();
  setSerialState('disconnected');

  try {
    /* 1 — generate */
    setFlashPhase('generating');
    setFlashProgress(null);
    pushLine('info', 'Generating C++ from blocks…');
    const result = generateCpp(requireWorkspace());

    const errors = result.diagnostics.filter((d) => d.level === 'error');
    const warnings = result.diagnostics.filter((d) => d.level === 'warning');
    for (const warning of warnings) pushLine('warn', warning.message);

    if (errors.length > 0) {
      for (const error of errors) pushLine('err', error.message);
      throw new Error('Fix the highlighted problems on the canvas, then flash again.');
    }
    if (result.pins.length > 0) {
      pushLine('info', `Pin plan: ${result.pins.map((p) => `GPIO${p.pin}(${p.peripheral})`).join(', ')}`);
    }
    if (result.libraries.length > 0) {
      pushLine('info', `Required libraries: ${result.libraries.join(', ')}`);
    }

    /* 2 — compile */
    setFlashPhase('compiling');
    pushLine('info', `Compiling for ${settings.fqbn} via ${settings.compileUrl} …`);
    let compile;
    try {
      compile = await compileSketch(result.cpp, {
        compileUrl: settings.compileUrl,
        fqbn: settings.fqbn,
        libraries: result.libraries,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      pushLine('warn', 'Falling back to sketch download. The program was not compiled.');
      downloadCurrentSketch(result.cpp, projectName);
      throw new Error(message);
    }

    if (compile.installedLibraries && compile.installedLibraries.length > 0) {
      pushLine('ok', `Installed missing libraries: ${compile.installedLibraries.join(', ')}`);
    }
    for (const line of compile.log.split('\n')) {
      if (line.trim()) pushLine('info', line);
    }
    if (!compile.ok || compile.images.length === 0) {
      throw new Error('Compilation failed. Check the compile log above.');
    }
    for (const image of compile.images) {
      pushLine('ok', `${image.name} → 0x${image.offset.toString(16)} (${Math.round(image.dataBase64.length * 0.75 / 1024)} KB)`);
    }

    /* 3 — flash */
    setFlashPhase('flashing');
    const flasher = new EspFlasher();
    const port = await flasher.flash(compile.images, {
      baudRate: settings.flashBaud,
      onLine: (line) => pushLine('flash', line),
      onProgress: (pct) => setFlashProgress(pct),
    });

    /* 4 — monitor */
    setFlashPhase('monitoring');
    setFlashProgress(null);
    await monitorExistingPort(port, settings.serialBaud, (line) => pushLine('serial', line));
    setSerialState('connected');
    pushLine('ok', `Firmware flashed and serial monitor open at ${settings.serialBaud} baud.`);
  } catch (err) {
    setFlashPhase('error');
    setFlashProgress(null);
    const mapped = describeSerialError(err);
    const message = err instanceof Error ? err.message : String(err);
    pushLine('err', `${mapped.title}: ${message}`);
    pushLine('info', mapped.detail);
  }
}

function downloadCurrentSketch(cpp: string, projectName: string): void {
  const blob = new Blob([cpp], { type: 'text/x-arduino' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${projectName || 'sketch'}.ino`;
  a.click();
  URL.revokeObjectURL(url);
}
